import React, { useState } from 'react';
import { Box, Paper, Button, Tabs, Tab, Stack, Tooltip, Divider, Typography, Checkbox, FormControlLabel, SvgIcon } from '@mui/material';
import { ColorPickerToolbar } from './ColorPickerToolbar';
import FillOptionsToolbar from './FillOptionsToolbar';
import ZoomToolbar from './ZoomToolbar';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import SquareOutlinedIcon from '@mui/icons-material/SquareOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import VignetteOutlinedIcon from '@mui/icons-material/VignetteOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import GestureIcon from '@mui/icons-material/Gesture';
import PolylineIcon from '@mui/icons-material/Polyline';
import RoundedCornerOutlinedIcon from '@mui/icons-material/RoundedCornerOutlined';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import FlipIcon from '@mui/icons-material/Flip';
import GridOnIcon from '@mui/icons-material/GridOn';
import TextureIcon from '@mui/icons-material/Texture';
import PaletteIcon from '@mui/icons-material/Palette';
import ColorizeIcon from '@mui/icons-material/Colorize';
import BackspaceIcon from '@mui/icons-material/Backspace';
import BrushIcon from '@mui/icons-material/Brush';
import ImageIcon from '@mui/icons-material/Image';

// Custom SVG icons for boolean operations showing two overlapping shapes
function UnionIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24">
      {/* Two overlapping circles, both filled to show union/combination */}
      <circle cx="9" cy="12" r="5" fill="currentColor" opacity="0.7" />
      <circle cx="15" cy="12" r="5" fill="currentColor" opacity="0.7" />
      {/* Outline to show the combined shape */}
      <path d="M9 7a5 5 0 0 0-5 5c0 1.3.5 2.5 1.3 3.4A5 5 0 0 0 9 20a5 5 0 0 0 3.7-1.6A5 5 0 0 0 20 12a5 5 0 0 0-5-5 5 5 0 0 0-3.7 1.6A5 5 0 0 0 9 7z" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </SvgIcon>
  );
}

function IntersectIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24">
      {/* Two circle outlines */}
      <circle cx="9" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      <circle cx="15" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      {/* Highlight only the intersection area */}
      <path d="M12 7a5 5 0 0 1 0 10 5 5 0 0 1 0-10z" fill="currentColor" opacity="0.9" />
    </SvgIcon>
  );
}

function SubtractIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24">
      {/* First circle (the one that remains) */}
      <circle cx="9" cy="12" r="5" fill="currentColor" opacity="0.8" />
      {/* Second circle (the cutter) - shown as outline */}
      <circle cx="15" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
      {/* Show the cut/removed intersection area */}
      <path d="M12 7.2a5 5 0 0 1 0 9.6 5 5 0 0 1 0-9.6z" fill="#f5f5f5" stroke="currentColor" strokeWidth="0.5" opacity="0.5" />
    </SvgIcon>
  );
}

function Ribbon({
  mode,
  onModeChange,
  color,
  setColor,
  fillColor,
  setFillColor,
  fillProps,
  setFillProps,
  onZoomIn,
  onZoomOut,
  onReset,
  onFit,
  onFitCanvas,
  onClear,
  onBooleanOperation,
  onSaveProject,
  onLoadProject,
  onImportSVG,
  onVectorize,
  realityMode,
  onRealityToggle,
  showEntryPoint,
  onShowEntryToggle,
  currentStitchIndex,
  setCurrentStitchIndex,
  totalStitches,
  isPlaying,
  setIsPlaying,
  onSimulateToggle,
  stitchColorMap,
  simulationSpeed,
  setSimulationSpeed,
  onMirror,
  isMirrorMode,
  setIsMirrorMode,
  onCrossAreaClick,
  crossViewMode,
  setCrossViewMode,
  crossTool,
  setCrossTool,
  onImportCrosses
}) {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    if (onSimulateToggle) {
      onSimulateToggle(newValue === 6); //ribbon for simulate!!
    }
  };

  const getButtonVariant = (buttonMode) =>
    mode === buttonMode ? 'contained' : 'outlined';

  // Generate CSS gradient for the slider background
  const generateSliderBackground = () => {
    if (!stitchColorMap || stitchColorMap.length === 0 || totalStitches === 0) {
      return '#ddd';
    }

    let currentPercent = 0;
    const gradientParts = [];
    stitchColorMap.forEach(item => {
      const startPercent = currentPercent;
      const endPercent = currentPercent + (item.length / totalStitches) * 100;
      gradientParts.push(`${item.color} ${startPercent}%`);
      gradientParts.push(`${item.color} ${endPercent}%`);
      currentPercent = endPercent;
    });

    return `linear-gradient(to right, ${gradientParts.join(', ')})`;
  };

  return (
    <Box
      sx={{
        position: 'relative', // Changed from fixed
        width: '100%',
        zIndex: 1000,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
      }}
    >
      <Paper elevation={0} sx={{ borderRadius: 0, backgroundColor: 'transparent' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="ribbon tabs"
          sx={{ minHeight: '36px', '& .MuiTab-root': { minHeight: '36px', py: 0.5, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab label="Home" />
          <Tab label="Create" />
          <Tab label="Stitches" />
          <Tab label="View" />
          <Tab label="Modify" />
          <Tab label="Crosses" />
          <Tab label="Simulate" />
        </Tabs>
      </Paper>

      <Box sx={{ px: 1, pt: 1, pb: 0.5, display: 'flex', alignItems: 'center' }}>
        {currentTab === 0 && (
          <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />} alignItems="center">
            {/* Tools Group */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }}>
                <Tooltip title="Select">
                  <Button variant={getButtonVariant('select')} onClick={() => onModeChange('select')} startIcon={<NearMeOutlinedIcon />} size="small">Select</Button>
                </Tooltip>
                <Tooltip title="Open Project">
                  <Button variant="outlined" onClick={onLoadProject} startIcon={<FolderOpenIcon />} size="small">Open</Button>
                </Tooltip>
                <Tooltip title="Save Project">
                  <Button variant="outlined" onClick={onSaveProject} startIcon={<SaveIcon />} size="small">Save</Button>
                </Tooltip>
                {/* Original Import SVG Button */}
                <Tooltip title="Import SVG File">
                  <Button variant="outlined" onClick={onImportSVG} startIcon={<FileUploadIcon />} size="small">Import</Button>
                </Tooltip>
                {/* Image to Vector Button */}
                <Tooltip title="Image to Vector">
                  <Button variant="outlined" onClick={onVectorize} startIcon={<VignetteOutlinedIcon />} size="small">Vectorize</Button>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Project</Typography>
            </Box>
          </Stack>
        )}

        {currentTab === 1 && (
          <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />} alignItems="center">
            {/* Shapes & Tools Group */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ maxWidth: '600px', justifyContent: 'center' }}>
                <Tooltip title="Rectangle">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('rectangle')} onClick={() => onModeChange('rectangle')}><SquareOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Square">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('square')} onClick={() => onModeChange('square')}><CheckBoxOutlineBlankOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Circle">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('circle')} onClick={() => onModeChange('circle')}><CircleOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Ellipse">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('ellipse')} onClick={() => onModeChange('ellipse')}><VignetteOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Pen">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('pen')} onClick={() => onModeChange('pen')}><GestureIcon /></Button>
                </Tooltip>
                <Tooltip title="Polyline (Straight Spline)">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('poly')} onClick={() => onModeChange('poly')}><PolylineIcon /></Button>
                </Tooltip>
                <Tooltip title="Spline">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('spline')} onClick={() => onModeChange('spline')}><RoundedCornerOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Interpolating Spline">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('curve')} onClick={() => onModeChange('curve')}><RoundedCornerOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="PolySpline (Ctrl: Line, Shift: Interp)">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('polyspline')} onClick={() => onModeChange('polyspline')}><GestureIcon /></Button>
                </Tooltip>
                <Tooltip title="Cross Area">
                  <Button sx={{ minWidth: '40px' }} size="small" variant={getButtonVariant('crossarea')} onClick={onCrossAreaClick}><GridOnIcon /></Button>
                </Tooltip>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Shapes & Tools</Typography>
            </Box>
          </Stack>
        )}

        {currentTab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <ColorPickerToolbar color={color} setColor={setColor} fillColor={fillColor} setFillColor={setFillColor} />
              <Divider orientation="vertical" flexItem />
              <FillOptionsToolbar fillProps={fillProps} setFillProps={setFillProps} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Style</Typography>
          </Box>
        )}

        {currentTab === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <ZoomToolbar
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                onReset={onReset}
                onFit={onFit}
                onFitCanvas={onFitCanvas}
              />
              <FormControlLabel
                control={<Checkbox checked={realityMode} onChange={(e) => onRealityToggle && onRealityToggle(e.target.checked)} size="small" />}
                label="Reality"
              />
              <FormControlLabel
                control={<Checkbox checked={typeof showEntryPoint !== 'undefined' ? showEntryPoint : false} onChange={(e) => onShowEntryToggle && onShowEntryToggle(e.target.checked)} size="small" />}
                label="Show entry point"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Zoom Controls</Typography>

          </Box>
        )}

        {currentTab === 4 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Union">
                <Button sx={{ minWidth: '40px' }} variant="outlined" onClick={() => onBooleanOperation('union')} size="small">
                  <UnionIcon />
                </Button>
              </Tooltip>
              <Tooltip title="Intersect">
                <Button sx={{ minWidth: '40px' }} variant="outlined" onClick={() => onBooleanOperation('intersection')} size="small">
                  <IntersectIcon />
                </Button>
              </Tooltip>
              <Tooltip title="Subtract">
                <Button sx={{ minWidth: '40px' }} variant="outlined" onClick={() => onBooleanOperation('difference')} size="small">
                  <SubtractIcon />
                </Button>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Mirror">
                <Button
                  sx={{ minWidth: '40px' }}
                  variant={isMirrorMode ? 'contained' : 'outlined'}
                  onClick={() => {
                    setIsMirrorMode && setIsMirrorMode(!isMirrorMode);
                    onMirror && onMirror(!isMirrorMode);
                  }}
                  size="small"
                >
                  <FlipIcon />
                </Button>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Boolean</Typography>
          </Box>
        )}

        {currentTab === 5 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Stitch View">
                  <Button variant={crossViewMode === 'stitch' ? 'contained' : 'outlined'} onClick={() => setCrossViewMode('stitch')} size="small"><TextureIcon /></Button>
                </Tooltip>
                <Tooltip title="Color View">
                  <Button variant={crossViewMode === 'color' ? 'contained' : 'outlined'} onClick={() => setCrossViewMode('color')} size="small"><PaletteIcon /></Button>
                </Tooltip>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Select">
                  <Button variant={crossTool === 'select' ? 'contained' : 'outlined'} onClick={() => setCrossTool('select')} size="small"><NearMeOutlinedIcon /></Button>
                </Tooltip>
                <Tooltip title="Paint">
                  <Button variant={crossTool === 'paint' ? 'contained' : 'outlined'} onClick={() => setCrossTool('paint')} size="small"><BrushIcon /></Button>
                </Tooltip>
                <Tooltip title="Erase">
                  <Button variant={crossTool === 'erase' ? 'contained' : 'outlined'} onClick={() => setCrossTool('erase')} size="small"><BackspaceIcon /></Button>
                </Tooltip>
                <Tooltip title="Pick Color">
                  <Button variant={crossTool === 'pick' ? 'contained' : 'outlined'} onClick={() => setCrossTool('pick')} size="small"><ColorizeIcon /></Button>
                </Tooltip>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Import Image">
                <Button variant="outlined" onClick={onImportCrosses} startIcon={<ImageIcon />} size="small">Import</Button>
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Cross Stitch Operations</Typography>
          </Box>
        )}

        {currentTab === 6 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
                <Button
                  variant="contained"
                  color={isPlaying ? "warning" : "primary"}
                  onClick={() => setIsPlaying(!isPlaying)}
                  size="small"
                  sx={{ minWidth: '40px' }}
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </Button>
              </Tooltip>
              <Tooltip title="Stop">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => { setIsPlaying(false); setCurrentStitchIndex(0); }}
                  size="small"
                  sx={{ minWidth: '40px' }}
                >
                  <StopIcon />
                </Button>
              </Tooltip>
              <Typography variant="body2" sx={{ minWidth: '120px' }}>
                Stitch: {currentStitchIndex} / {totalStitches}
              </Typography>
              <input
                type="range"
                min="0"
                max={totalStitches}
                value={currentStitchIndex}
                onChange={(e) => setCurrentStitchIndex(parseInt(e.target.value, 10))}
                style={{
                  width: '300px',
                  height: '10px',
                  borderRadius: '5px',
                  background: generateSliderBackground(),
                  outline: 'none',
                  opacity: '0.8',
                  appearance: 'none',
                  cursor: 'pointer',
                  border: '1px solid #ccc'
                }}
              />
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption">Speed:</Typography>
                  <input
                    type="range"
                    min="0"
                    max="190"
                    value={200 - simulationSpeed}
                    onChange={(e) => setSimulationSpeed && setSimulationSpeed(200 - parseInt(e.target.value, 10))}
                    style={{
                      width: '100px',
                      height: '8px',
                      borderRadius: '4px',
                      background: 'linear-gradient(to right, #2196f3, #f44336)',
                      outline: 'none',
                      appearance: 'none',
                      cursor: 'pointer'
                    }}
                  />
                  <Typography variant="body2" sx={{ minWidth: '50px' }}>
                    {Math.round((200 - simulationSpeed) / 2)}%
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">Slow → Fast</Typography>
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>Simulation</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Ribbon;
